import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import ModulePaymentPlans from '../components/ModulePaymentPlans';
import ModuleCoupons from '../components/ModuleCoupons';
import useAuthStore from '../store/authStore';
import api, { academicService, enrollmentService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { CURRENCY_OPTIONS } from '../utils/currency';

function CreateModule() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [createdModule, setCreatedModule] = useState(null);
  const [grades, setGrades] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [myPaymentAccounts, setMyPaymentAccounts] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    grade: '',
    teachers: [],
    image: null,
    intro_video: null,
    intro_video_url: '',
    price: '0.00',
    currency: 'USD',
    enrollment_directives: '',
    sequential_progress: false,
    requires_approval: true,
    payment_account_ids: [],
  });
  const [errors, setErrors] = useState({});

  const isAdmin = user?.roles?.some(role => role.name?.toLowerCase() === 'administrator');
  const isTeacher = user?.roles?.some(role => role.name?.toLowerCase() === 'teacher');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!isTeacher && !isAdmin) {
      addNotification(t('Only teachers or administrators can create modules'), 'error');
      navigate('/courses');
      return;
    }

    fetchGrades();
    fetchMyPaymentAccounts();
    if (isAdmin) fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, user]);

  const fetchMyPaymentAccounts = async () => {
    try {
      const res = await enrollmentService.getPaymentAccounts();
      const data = res.data;
      const list = Array.isArray(data) ? data : data.results || [];
      setMyPaymentAccounts(list.filter((a) => !a.is_platform_default));
    } catch (error) {
      // non-fatal — the picker just stays empty
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await academicService.getGrades();
      const gradesData = response.data || response;
      setGrades(Array.isArray(gradesData) ? gradesData : gradesData.results || []);
    } catch (error) {
      addNotification(t('Failed to load grades. Please refresh the page.'), 'error');
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await api.get('/users/', { params: { role: 'teacher' } });
      const data = response.data || response;
      setTeachers(Array.isArray(data) ? data : data.results || []);
    } catch (error) {
      addNotification(t('Failed to load teachers.'), 'error');
    }
  };

  const toggleTeacher = (teacherId) => {
    setFormData(prev => ({
      ...prev,
      teachers: prev.teachers.includes(teacherId)
        ? prev.teachers.filter(id => id !== teacherId)
        : [...prev.teachers, teacherId]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, image: file }));
  };

  const handleIntroVideoChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, intro_video: file }));
  };

  const handleIntroVideoUrlChange = (e) => {
    setFormData(prev => ({ ...prev, intro_video_url: e.target.value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('Module name is required');
    if (!formData.code.trim()) newErrors.code = t('Module code is required');
    if (!formData.grade) newErrors.grade = t('Grade is required');
    if (isAdmin && formData.teachers.length === 0) newErrors.teachers = t('Select at least one teacher');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      // If an image is included, use multipart/form-data
      // Create module first (JSON) then upload thumbnail if provided
      const submitData = {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        grades: [parseInt(formData.grade)], // Backend expects grades as an array
        price: parseFloat(formData.price || 0),
        currency: formData.currency,
        enrollment_directives: formData.enrollment_directives,
        sequential_progress: formData.sequential_progress,
        requires_approval: formData.requires_approval,
        payment_accounts: formData.payment_account_ids,
        intro_video_url: formData.intro_video_url || ''
      };
      if (isAdmin && formData.teachers.length > 0) {
        submitData.teachers = formData.teachers;
      }
      const response = await academicService.createModule(submitData);
      const created = response.data || response;

      if (formData.image && created?.id) {
        const fd = new FormData();
        fd.append('thumbnail', formData.image);
        await api.patch(`/academics/modules/${created.id}/`, fd);
      }

      if (formData.intro_video && created?.id) {
        const fd2 = new FormData();
        fd2.append('intro_video', formData.intro_video);
        await api.patch(`/academics/modules/${created.id}/`, fd2);
      }

      addNotification(t('Module created successfully!'), 'success');
      // Stay on this same page and reveal pricing plan / coupon setup for
      // the course just created, instead of sending the teacher off to a
      // separate page (or worse, back to the course list to hunt for one) -
      // creation and pricing happen in one continuous place.
      if (created?.id) {
        setCreatedModule(created);
      } else {
        navigate('/courses');
      }
    } catch (error) {
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          setErrors(errorData);
          // Show first error message
          const firstError = Object.values(errorData)[0];
          addNotification(`${t('Failed')}: ${typeof firstError === 'string' ? firstError : JSON.stringify(firstError)}`, 'error');
        } else {
          addNotification(`${t('Failed to create module')}: ${errorData}`, 'error');
        }
      } else if (error.message) {
        addNotification(`${t('Failed to create module')}: ${error.message}`, 'error');
      } else {
        addNotification(t('Failed to create module. Please try again.'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Create Module')} subtitle={t('Add a new course module')} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
          {createdModule ? (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-md p-6 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{t('"{{name}}" was created', { name: createdModule.name })}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('Add pricing plans and discount coupons for this course now, or skip and use the platform-wide defaults.')}
                  </p>
                </div>
              </div>

              <ModulePaymentPlans moduleId={createdModule.id} />
              <ModuleCoupons moduleId={createdModule.id} />

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate(`/modules/${createdModule.id}`)}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                  {t('Done — Go to Course')}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Module Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Module Name')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('e.g., Introduction to Mathematics')}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                {/* Module Code */}
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Module Code')} *
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.code ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('e.g., MATH101')}
                  />
                  {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
                </div>

                {/* Grade */}
                <div>
                  <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Grade Level')} *
                  </label>
                  <select
                    id="grade"
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.grade ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">{t('Select a grade')}</option>
                    {grades.map(grade => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name}
                      </option>
                    ))}
                  </select>
                  {errors.grade && <p className="mt-1 text-sm text-red-600">{errors.grade}</p>}
                </div>

                {/* Teacher assignment (admin only) */}
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Assign Teachers')}
                    </label>
                    <div className="border border-gray-300 rounded-lg divide-y max-h-48 overflow-y-auto">
                      {teachers.length === 0 ? (
                        <p className="text-sm text-gray-500 p-3">{t('No approved teachers available')}</p>
                      ) : (
                        teachers.map(teacher => (
                          <label key={teacher.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.teachers.includes(teacher.id)}
                              onChange={() => toggleTeacher(teacher.id)}
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">{teacher.first_name} {teacher.last_name} <span className="text-gray-400">({teacher.email})</span></span>
                          </label>
                        ))
                      )}
                    </div>
                    {errors.teachers && <p className="mt-1 text-sm text-red-600">{errors.teachers}</p>}
                  </div>
                )}

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Description')}
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={t('Provide a brief description of the module...')}
                  />
                </div>

                {/* Price */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">{t('Price')}</label>
                    <input
                      type="number"
                      step="0.01"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">{t('Currency')}</label>
                    <select
                      id="currency"
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 bg-white"
                    >
                      {CURRENCY_OPTIONS.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Enrollment Directives */}
                <div>
                  <label htmlFor="enrollment_directives" className="block text-sm font-medium text-gray-700 mb-2">{t('Enrollment Instructions (optional)')}</label>
                  <textarea
                    id="enrollment_directives"
                    name="enrollment_directives"
                    value={formData.enrollment_directives}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={t('Provide instructions or directives for students when enrolling (e.g., payment steps).')}
                  />
                </div>

                {/* Sequential progress */}
                <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                  <input
                    id="sequential_progress"
                    name="sequential_progress"
                    type="checkbox"
                    checked={formData.sequential_progress}
                    onChange={(e) => setFormData(prev => ({ ...prev, sequential_progress: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="sequential_progress" className="text-sm text-gray-700">
                    <span className="font-medium block">{t('Lock lessons in order')}</span>
                    {t('Students must complete each lesson before the next one unlocks.')}
                  </label>
                </div>

                {/* Requires approval */}
                <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                  <input
                    id="requires_approval"
                    name="requires_approval"
                    type="checkbox"
                    checked={formData.requires_approval}
                    onChange={(e) => setFormData(prev => ({ ...prev, requires_approval: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="requires_approval" className="text-sm text-gray-700">
                    <span className="font-medium block">{t('Require approval before students can access this course')}</span>
                    {t('Turn this off for free/open courses — students get access immediately, with no teacher or admin approval step.')}
                  </label>
                </div>

                {/* Payment accounts */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">{t('Payment Accounts Shown to Students')}</label>
                    <Link to="/payment-accounts" className="text-xs text-primary-600 hover:underline">{t('+ Manage my payment accounts')}</Link>
                  </div>
                  <div className="border border-gray-300 rounded-lg divide-y max-h-48 overflow-y-auto">
                    {myPaymentAccounts.length === 0 ? (
                      <p className="text-sm text-gray-500 p-3">{t("You haven't added any payment accounts yet — the platform's default account will be shown to students instead.")}</p>
                    ) : (
                      myPaymentAccounts.map((acc) => (
                        <label key={acc.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.payment_account_ids.includes(acc.id)}
                            onChange={() => setFormData(prev => ({
                              ...prev,
                              payment_account_ids: prev.payment_account_ids.includes(acc.id)
                                ? prev.payment_account_ids.filter((x) => x !== acc.id)
                                : [...prev.payment_account_ids, acc.id]
                            }))}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">{acc.label} <span className="text-gray-400">({acc.method_type})</span></span>
                        </label>
                      ))
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {t("If none are selected, students see the platform's default payment account instead.")}
                    {' '}{t("You'll be taken straight to pricing plan and coupon setup for this course right after creating it.")}
                  </p>
                </div>

                {/* Image (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Module Image (optional)')}</label>
                  <div className="flex items-center gap-4">
                    <label htmlFor="image" className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                      <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7M3 7l9 6 9-6" />
                      </svg>
                      {t('Upload image')}
                    </label>
                    <input
                      id="image"
                      name="thumbnail"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="text-sm text-gray-600">
                      {formData.image ? (
                        <span className="font-medium">{formData.image.name}</span>
                      ) : (
                        <span className="text-gray-500">{t('No file chosen')}</span>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{t('Supported formats: JPG, PNG. Max 5MB.')}</p>
                </div>

                {/* Intro Video (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Intro Video (optional)')}</label>
                  <div className="flex items-center gap-4">
                    <label htmlFor="intro_video" className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                      {t('Upload intro video')}
                    </label>
                    <input
                      id="intro_video"
                      name="intro_video"
                      type="file"
                      accept="video/*"
                      onChange={handleIntroVideoChange}
                      className="hidden"
                    />
                    <div className="text-sm text-gray-600">
                      {formData.intro_video ? (
                        <span className="font-medium">{formData.intro_video.name}</span>
                      ) : (
                        <span className="text-gray-500">{t('No file chosen')}</span>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{t('Supported: mp4, webm. Max 50MB.')}</p>
                </div>

                <div>
                  <label htmlFor="intro_video_url" className="block text-sm font-medium text-gray-700 mb-2">{t('Or Intro Video URL (YouTube etc.)')}</label>
                  <input
                    id="intro_video_url"
                    name="intro_video_url"
                    type="text"
                    value={formData.intro_video_url}
                    onChange={handleIntroVideoUrlChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('Provide a video link instead of uploading a file.')}</p>
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/courses')}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t('Creating...') : t('Create Module')}
                  </button>
                </div>
              </form>
            </div>
          )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default CreateModule;
